import { useEffect, useState } from "react";
import { CrownIcon, ShieldCheckIcon, UserIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@primitive/dialog";
import { Button } from "@primitive/button";
import { Label } from "@primitive/label";
import { RadioGroup, RadioGroupItem } from "@primitive/radio-group";
import { useLoadingTimeouts } from "@hooks/useLoadingTimeouts";
import { UserTypeDialogProps } from "@interfaces";
import { SelectableUserType } from "@types";
import { cn } from "@utils";

const OPTIONS: Array<{
  value: SelectableUserType;
  title: string;
  badge: string;
  description: string;
  icon: typeof UserIcon;
}> = [
  {
    value: "community-user",
    badge: "Participate",
    title: "Community Member",
    description: "Join campaigns, complete tasks, and track rewards and progress.",
    icon: UserIcon,
  },
  {
    value: "community-admin",
    title: "Community Admin",
    badge: "Operate",
    description: "Manage campaigns and help operate the communities you work with.",
    icon: ShieldCheckIcon,
  },
  {
    value: "community-owner",
    title: "Community Owner",
    badge: "Lead",
    description: "Create or manage communities, team access, and key settings.",
    icon: CrownIcon,
  },
];

const LOADING_TIMEOUT_KEYS = ["save"] as const;

export function UserTypeDialog({
  open,
  isSaving,
  onConfirm,
  value,
  onOpenChange,
  allowedTypes = ["community-owner", "community-user"],
  mode = "switch",
}: UserTypeDialogProps) {
  const [selected, setSelected] = useState<SelectableUserType | null>(null);
  const [saveTimedOut, setSaveTimedOut] = useState(false);
  const { startTimeout, clearTimeout } = useLoadingTimeouts(LOADING_TIMEOUT_KEYS);

  useEffect(() => {
    if (open) {
      setSelected(value ?? null);
    }
  }, [open, value]);

  useEffect(() => {
    if (isSaving) {
      setSaveTimedOut(false);
      startTimeout("save", () => setSaveTimedOut(true));
    } else {
      setSaveTimedOut(false);
      clearTimeout("save");
    }
  }, [clearTimeout, isSaving, startTimeout]);

  const isLocked = !!isSaving && !saveTimedOut;
  const isOnboarding = mode === "onboarding";
  const visibleOptions = OPTIONS.filter((option) => allowedTypes.includes(option.value));
  const selectedOption = visibleOptions.find((option) => option.value === selected);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isLocked) return;
    (onOpenChange ?? (() => { }))(nextOpen);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent className={cn(
        "sm:max-w-xl **:data-[slot='dialog-close']:hidden",
        isLocked ? "pointer-events-none opacity-75" : ""
      )}>
        {isLocked && (
          <div
            className="absolute inset-0 z-10 cursor-not-allowed bg-background/40"
            aria-hidden="true"
          />
        )}
        <DialogHeader className="space-y-2">
          <DialogTitle>
            {isOnboarding ? "Choose your starting view" : "Switch view"}
          </DialogTitle>
          <DialogDescription>
            {isOnboarding
              ? "Pick how you want to use the dashboard first. You can switch views later from your profile menu."
              : "Change what you want to do right now. This only changes your dashboard view, and you can switch back anytime."}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={selected ?? undefined}
          onValueChange={(nextValue) => setSelected(nextValue as SelectableUserType)}
          className="gap-3"
          disabled={isLocked}
        >
          {visibleOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selected === option.value;
            const isCurrent = value === option.value;
            const optionId = `user-type-${option.value}`;

            return (
              <Label
                key={option.value}
                htmlFor={optionId}
                className={cn(
                  "items-start gap-3 rounded-2xl border p-4 transition-colors",
                  isSelected
                    ? "border-primary bg-primary/6 shadow-sm"
                    : "border-border/70 bg-background hover:border-primary/40 hover:bg-muted/30",
                  isLocked ? "cursor-not-allowed opacity-60 hover:border-border/70 hover:bg-background" : "cursor-pointer"
                )}
              >
                <RadioGroupItem
                  id={optionId}
                  value={option.value}
                  className="mt-1"
                />

                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span
                    className={cn(
                      "mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full border",
                      isSelected ? "border-primary/30 bg-primary/10 text-primary" : "border-border bg-muted/40 text-muted-foreground"
                    )}
                  >
                    <Icon className="size-4" />
                  </span>

                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold text-foreground">
                        {option.title}
                      </span>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {option.badge}
                      </span>
                      {isCurrent && !isOnboarding && (
                        <span className="rounded-full border border-primary/20 bg-primary/8 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-primary">
                          Current view
                        </span>
                      )}
                    </div>

                    <p className="text-sm leading-6 text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </div>
              </Label>
            );
          })}
        </RadioGroup>

        <DialogFooter className="items-center justify-between gap-3 pt-2 sm:flex-row">
          <p className="text-sm text-muted-foreground">
            {isOnboarding
              ? "You are choosing how the dashboard should open for you first."
              : "Your account access stays the same. Only the dashboard view changes."}
          </p>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            {onOpenChange && (
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isLocked}
              >
                Cancel
              </Button>
            )}
          <Button
            onClick={() => selected && onConfirm(selected)}
            disabled={isLocked || selected === null || (!isOnboarding && selected === value)}
            className="min-w-36"
          >
            {isSaving
              ? "Saving..."
              : isOnboarding
                ? "Continue"
                : selectedOption
                  ? `Switch to ${selectedOption.title}`
                  : "Switch view"}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
